'use client';
/**
 * Меню профиля в правом верхнем углу: настройки, админ-консоль, выход.
 *
 * Выход раньше жил иконкой внизу сайдбара и при переезде на новую оболочку
 * потерялся. Здесь он там, где его ищут, и — в отличие от прежней версии —
 * сначала гасит серверную сессию (`POST /auth/logout` отзывает jti и чистит
 * HttpOnly-куки), а уже потом чистит клиентское состояние. Раньше выход был
 * чисто клиентским: cookie оставалась валидной до истечения срока.
 */
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { Settings, LogOut, ExternalLink, UserRound } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '@/lib/api';
import { useAuthStore } from '@/store/auth.store';
import { useT } from '@/lib/useT';
import { cx, Avatar } from '@/components/ui';

export default function UserMenu() {
  const t = useT();
  const router = useRouter();
  const { user, logout } = useAuthStore();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onEsc); };
  }, [open]);

  const label = (key: string, fallback: string) => {
    const v = t(key);
    return !v || v === key ? fallback : v;
  };

  const name = `${user?.name ?? ''} ${user?.surname ?? ''}`.trim() || 'User';
  const isSuperAdmin = user?.role === 'SUPER_ADMIN' || user?.orgRole === 'OWNER';

  const signOut = async () => {
    setBusy(true);
    try {
      // Сессию гасим на сервере — иначе кука остаётся валидной.
      await api.post('/auth/logout', {});
    } catch {
      // Сеть отвалилась — локальный выход всё равно выполняем, иначе человек
      // останется «внутри» на общем компьютере.
    } finally {
      logout();
      setBusy(false);
      toast.success(label('logout_done', 'Signed out'));
      router.push('/login');
    }
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu" aria-expanded={open}
        className={cx(
          'flex items-center gap-2 h-9 pl-1.5 pr-2 rounded-[var(--radius-sm)] transition-colors',
          'hover:bg-[var(--surface-hover)] focus-visible:outline-none focus-visible:shadow-[var(--focus-ring)]',
          open && 'bg-[var(--surface-hover)]',
        )}
      >
        <Avatar name={name} size={26} />
        <span className="hidden xl:block leading-tight text-left">
          <span className="block text-[12.5px] font-medium text-[var(--text-primary)] truncate max-w-[130px]">{name}</span>
          <span className="block text-[11px] text-[var(--text-muted)] truncate max-w-[130px]">{user?.orgRole ?? user?.role ?? ''}</span>
        </span>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-[calc(100%+6px)] w-[232px] py-1 z-50
                     bg-[var(--surface-raised)] border border-[var(--border-strong)]
                     rounded-[var(--radius-md)] shadow-[var(--shadow-md)]"
        >
          <div className="px-3 py-2 border-b border-[var(--border)]">
            <p className="text-[12.5px] font-medium text-[var(--text-primary)] truncate">{name}</p>
            {user?.orgName && <p className="text-[11px] text-[var(--text-muted)] truncate">{user.orgName}</p>}
          </div>

          <MenuLink href="/dashboard/settings" icon={UserRound} onClick={() => setOpen(false)}>
            {label('nav_settings', 'Settings')}
          </MenuLink>
          <MenuLink href="/dashboard/settings/security" icon={Settings} onClick={() => setOpen(false)}>
            {label('nav_security', 'Security')}
          </MenuLink>
          {isSuperAdmin && (
            <a
              href="https://admin.logixa.one" target="_blank" rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2.5 h-9 px-3 text-[13px] text-[var(--text-secondary)] hover:bg-[var(--surface-hover)] hover:text-[var(--text-primary)] transition-colors"
            >
              <ExternalLink size={14} className="text-[var(--text-muted)]" />
              {label('open_admin_console', 'Admin console')}
            </a>
          )}

          <div className="my-1 h-px bg-[var(--border)]" />

          <button
            onClick={signOut}
            disabled={busy}
            className="flex items-center gap-2.5 w-full h-9 px-3 text-[13px] text-[var(--danger)] hover:bg-[var(--danger-subtle)] transition-colors disabled:opacity-50"
          >
            <LogOut size={14} />
            {busy ? label('logout_progress', 'Signing out…') : label('logout', 'Sign out')}
          </button>
        </div>
      )}
    </div>
  );
}

function MenuLink({ href, icon: Icon, children, onClick }: {
  href: string; icon: typeof Settings; children: React.ReactNode; onClick: () => void;
}) {
  return (
    <Link
      href={href} onClick={onClick}
      className="flex items-center gap-2.5 h-9 px-3 text-[13px] text-[var(--text-secondary)] hover:bg-[var(--surface-hover)] hover:text-[var(--text-primary)] transition-colors"
    >
      <Icon size={14} className="text-[var(--text-muted)]" />
      {children}
    </Link>
  );
}
